import { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';

interface TabsContextValue {
  activeTab: string;
  setActiveTab: (id: string) => void;
}

const TabsContext = createContext<TabsContextValue | undefined>(undefined);

interface TabsProps {
  defaultValue?: string;
  children: ReactNode;
  className?: string;
}

export function Tabs({ defaultValue, children, className = '' }: TabsProps) {
  const [activeTab, setActiveTab] = useState<string>(defaultValue || '');

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <div className={`w-full ${className}`}>{children}</div>
    </TabsContext.Provider>
  );
}

interface TabsListProps {
  children: ReactNode;
  className?: string;
}

export function TabsList({ children, className = '' }: TabsListProps) {
  return (
    <div role='tablist' className={`flex md:w-max rounded-lg p-1 bg-gray-100 ${className}`}>
      {children}
    </div>
  );
}

interface TabsTriggerProps {
  value: string;
  children: ReactNode;
  className?: string;
  icon?: ReactNode;
  disabled?: boolean;
}

export function TabsTrigger({ value, children, className = '', icon, disabled }: TabsTriggerProps) {
  const context = useContext(TabsContext);

  if (!context) {
    throw new Error('TabsTrigger must be used within a Tabs component');
  }

  const { activeTab, setActiveTab } = context;
  const isActive = activeTab === value;

  return (
    <button
      role='tab'
      disabled={disabled}
      data-cy='tab-trigger'
      aria-selected={isActive}
      onClick={() => setActiveTab(value)}
      className={`flex items-center justify-center w-full min-w-[100px] px-7 py-1.5 text-sm font-medium transition-all outline-none ${
        isActive
          ? 'bg-white text-gray-800 !font-bold shadow-sm rounded-md'
          : disabled
            ? 'text-gray-400 cursor-not-allowed'
            : 'text-gray-600 hover:text-gray-800'
      } ${className}`}
    >
      {icon && <span className='mr-2 inline-flex items-center size-4'>{icon}</span>}
      {children}
    </button>
  );
}

interface TabsContentProps {
  value: string;
  children: ReactNode;
  className?: string;
}

export function TabsContent({ value, children, className = '' }: TabsContentProps) {
  const context = useContext(TabsContext);

  if (!context) {
    throw new Error('TabsContent must be used within a Tabs component');
  }

  const { activeTab } = context;
  const isActive = activeTab === value;

  if (!isActive) return null;

  return (
    <div
      role='tabpanel'
      className={`mt-2 p-4 md:p-6 focus:outline-none rounded-lg border-gray-200 border ${className}`}
    >
      {children}
    </div>
  );
}
